import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, canonicalBase, fixAssetUrl } from "../lib/api";
import SEO from "../components/SEO";
import RichTextView from "../components/RichTextView";
import NotFound from "./NotFound";

export function BlogList() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/blog").then((r) => setPosts(r.data)).finally(() => setLoading(false));
  }, []);
  return (
    <div className="pt-8 pb-20" data-testid="blog-list">
      <SEO
        title="Blog – tipy o softwaru a licencích"
        description="Aktuality a praktické návody o licenčním softwaru, aktivacích a digitálním obsahu."
        keywords="blog, software, licence, návody, aktivace"
      />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="text-xs uppercase tracking-widest text-blue-600 font-semibold mb-3">Blog</div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">Aktuality a tipy</h1>
          <p className="text-zinc-600 mt-3">Vše o licenčním softwaru, aktivacích a digitálním obsahu.</p>
        </div>
        {loading ? (
          <div className="text-center text-zinc-500">Načítání…</div>
        ) : posts.length === 0 ? (
          <div className="text-center text-zinc-500 py-12">Zatím žádné články. Brzy přidáme.</div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {posts.map((p) => (
              <Link key={p.id} to={`/blog/${p.slug}`} className="group bg-white border border-zinc-200 rounded-2xl overflow-hidden hover:shadow-lg transition-shadow" data-testid={`blog-post-${p.slug}`}>
                {p.cover_image && (
                  <div className="aspect-[16/9] bg-zinc-100 overflow-hidden">
                    <img src={fixAssetUrl(p.cover_image)} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }}/>
                  </div>
                )}
                <div className="p-5">
                  <div className="text-xs text-zinc-500 mb-2">{new Date(p.created_at).toLocaleDateString("cs-CZ")}</div>
                  <h2 className="font-display font-bold text-lg mb-2 group-hover:text-blue-600 transition-colors">{p.title}</h2>
                  <p className="text-sm text-zinc-600 line-clamp-3">{p.excerpt}</p>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export function BlogDetail() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);
  useEffect(() => {
    setNotFound(false); setPost(null);
    api.get(`/blog/${slug}`).then((r) => setPost(r.data)).catch(() => setNotFound(true));
  }, [slug]);
  if (notFound) return <NotFound/>;
  if (!post) return <div className="pt-10 text-center text-zinc-500">Načítání…</div>;
  const base = canonicalBase();
  const articleLd = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    description: post.excerpt || "",
    image: post.cover_image ? [post.cover_image] : undefined,
    datePublished: post.created_at,
    dateModified: post.updated_at || post.created_at,
    author: { "@type": "Organization", name: window.__siteSettings?.seo_site_name || "Správný Směr" },
    mainEntityOfPage: `${base}/blog/${post.slug}`,
  };
  return (
    <article className="pt-8 pb-20" data-testid={`blog-detail-${slug}`}>
      <SEO title={post.title} description={post.excerpt || post.title} image={post.cover_image} type="article" jsonLd={articleLd} />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/blog" className="text-sm text-blue-600 mb-6 inline-block">← Blog</Link>
        {post.cover_image && (
          <div className="aspect-[16/9] bg-zinc-100 rounded-2xl overflow-hidden mb-8">
            <img src={fixAssetUrl(post.cover_image)} alt={post.title} className="w-full h-full object-cover" onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }}/>
          </div>
        )}
        <div className="text-xs text-zinc-500 mb-3">{new Date(post.created_at).toLocaleDateString("cs-CZ")}</div>
        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-6 break-words">{(post.title || "").replace(/\u00a0/g, " ")}</h1>
        {post.excerpt && <p className="text-lg text-zinc-600 mb-8 break-words">{post.excerpt.replace(/\u00a0/g, " ")}</p>}
        <RichTextView html={post.content} />
      </div>
    </article>
  );
}
